// The first ten minutes (CHANGELOG 6.7): short first runs with guaranteed moments,
// an equip tutorial, and a step log for the onboarding funnel.

import type { ZoneId } from './types';

/**
 * Steps in the order a new player meets them. Times are ms since the survivor was created. 2. 10. 2026
 * (package 1, 11.a): `exp1_viewed` (the first report opened) apart from `exp1_back` (its loot claimed);
 * a survivor from before it counts as having viewed once `exp1_back` is there.
 */
export const ONBOARDING_STEPS = ['created', 'exp1_sent', 'exp1_viewed', 'exp1_back', 'equipped', 'exp2_sent', 'exp2_back', 'contract', 'craft', 'done'] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/**
 * Steps the onboarding does not need to finish (`done` is the second run back, the contract
 * board and a craft): the equip tutorial can be skipped. The pilot report keeps them out of the
 * funnel and shows them as a share of the players who got to `after`.
 */
export const ONBOARDING_OPTIONAL_STEPS: readonly { step: OnboardingStep; after: OnboardingStep }[] = [{ step: 'equipped', after: 'exp1_back' }];

/** Fields a player can change on the one-screen creation (docs/onboarding.md, section 5). */
export const CREATION_FIELDS = ['look', 'name', 'name_random', 'background'] as const;
export type CreationField = (typeof CREATION_FIELDS)[number];

export interface OnboardingState {
  /** Survivors from before 6.7 (and players who finished) skip everything. */
  done: boolean;
  startedAt?: string;
  steps: Partial<Record<OnboardingStep, number>>;
  /** The guaranteed Uncommon from the first run, for the equip tutorial. */
  tutorialItemId?: string | null;
  /**
   * Package 4, part A (7. 10. 2026): an account made after the deploy opens its systems by day and session (schedule.ts)
   * and is guided through each one the first time (guided.ts). Kept through a season's wipe, like the rest of this.
   */
  schedule?: import('./schedule').ScheduleState;
  guided?: import('./guided').GuidedState;
  /** Package 4, part A.4: the First Week (21 goals over 7 days). */
  firstWeek?: import('./firstWeek').FirstWeekState;
  /** 9. 10. 2026 (owner): the starter contract (`STARTER_CONTRACT`), accounts made after the deploy of the next package. */
  starter?: StarterContractState;
  /**
   * 11. 10. 2026 (owner, the Market): when the ACCOUNT was made (`users.created_at`, ms), kept here for the checks that
   * have only the survivor (the schedule, the unlocks); the survivor is made again every season. The server writes it at
   * creation and at a session's start; the Market's own checks read the user row.
   */
  accountAt?: number;
}

/**
 * Package 4, part A.3 (owner, 7. 10. 2026): on a schedule the first run is an event: its guaranteed find is Uncommon or,
 * one time in seven or so, Rare; at its end Tobias meets the survivor at the gate of Station 9 with a small gift (the
 * Trader as a system still opens on day 3).
 */
export const FIRST_RUN_EVENT = {
  rareChance: 0.15,
  tobias: {
    lines: ['A man with a handcart is waiting at the gate of Station 9.', '"Name\'s Tobias. I trade. These are on me. The next ones won\'t be."'],
    gift: { bandage: 2 } as import('./types').StackMap,
  },
};

/**
 * 9. 10. 2026 (owner): every new account starts with one easy contract, "Come back from Calder Woods 3 times" (the two
 * first runs count); claimed at the end of the first session. 10. 10. 2026 (owner): it is the easy contract of the board
 * (the board comes after the second run, so it shows 2/3 then) until claimed, not a card of its own.
 */
export const STARTER_CONTRACT = { zone: 'forest' as ZoneId, target: 3, reward: { scrap: 30, bandage: 2 } as import('./types').StackMap, text: 'Come back from Calder Woods 3 times' };

export interface StarterContractState {
  progress: number;
  claimedAt?: number;
}

export const ONBOARDING = {
  /** First run: Forest, 60 seconds, a guaranteed Uncommon item. */
  firstRun: { zones: ['forest'] as ZoneId[], minutes: 1 },
  /** Second run: 3 minutes, a guaranteed choice event and a friendly ghost. */
  secondRun: { zones: ['forest', 'gas_station'] as ZoneId[], minutes: 3 },
  /** Target from the changelog: every step within ten minutes. */
  targetMinutes: 10,
} as const;

/** Which onboarding run this would be (1 or 2), or null for a normal expedition. */
export function onboardingRun(state: OnboardingState | null | undefined, expeditionCount: number, zone: ZoneId): 1 | 2 | null {
  if (!state || state.done) return null;
  if (expeditionCount === 0 && ONBOARDING.firstRun.zones.includes(zone)) return 1;
  if (expeditionCount === 1 && ONBOARDING.secondRun.zones.includes(zone)) return 2;
  return null;
}

export function onboardingMinutes(run: 1 | 2): number {
  return run === 1 ? ONBOARDING.firstRun.minutes : ONBOARDING.secondRun.minutes;
}

/**
 * "Save your survivor" card on Camp (docs/onboarding.md, section 6): a guest account lives in one browser. 20. 10. 2026
 * (owner, pilot report): the card comes when a guest first has something worth keeping, a Rare or better item (in the
 * Collection book) or level 3; after `Later` it comes back at the earliest `againAfterDays` later, as long as no sign-in is
 * linked (before: after the first report and the first equip, again after the third run, at most twice).
 */
export const LINK_PROMPT = { level: 3, rarities: ['rare', 'epic', 'legendary'], againAfterDays: 3 } as const;

export interface LinkPromptState {
  /** Times the player pressed Later. */
  dismissed: number;
  /** Appearance (1, 2, …) whose `link_prompt_shown` was already logged. */
  shown: number;
  /** 20. 10. 2026: when Later was last pressed (ms). */
  lastDismissedAt?: number;
}

/** 20. 10. 2026 (owner): what makes the card come: a Rare or better item found (a book key is `<item>:<rarity>`), or the level. */
export function linkPromptTrigger(level: number, collectionFound: readonly string[]): 'rare' | 'level' | null {
  const rarities: readonly string[] = LINK_PROMPT.rarities;
  if (collectionFound.some((k) => rarities.includes(k.slice(k.lastIndexOf(':') + 1)))) return 'rare';
  return level >= LINK_PROMPT.level ? 'level' : null;
}

/**
 * Krug 5 (K6.2): the offer to dismantle Common gear automatically. It shows when Storage first
 * reaches 70% while the rule is off; after `Not now`, once more over 85%; never more than twice,
 * never once accepted, and never during the onboarding (the first five runs).
 */
export const AUTO_DISMANTLE_PROMPT = { firstAt: 0.7, againAt: 0.85, maxTimes: 2, afterRuns: 5 } as const;

export interface AutoDismantlePromptState {
  /** Times the player pressed Not now. */
  dismissed: number;
  /** Appearance (1 or 2) whose `autodismantle_prompt_shown` was already logged. */
  shown: number;
  accepted?: boolean;
}

export function autoDismantlePromptDue(state: Partial<AutoDismantlePromptState> | null | undefined, o: { fill: number; ruleOn: boolean; inOnboarding: boolean }): boolean {
  if (o.ruleOn || o.inOnboarding || state?.accepted) return false;
  const dismissed = state?.dismissed ?? 0;
  if (dismissed >= AUTO_DISMANTLE_PROMPT.maxTimes) return false;
  return o.fill >= (dismissed === 0 ? AUTO_DISMANTLE_PROMPT.firstAt : AUTO_DISMANTLE_PROMPT.againAt);
}

/** 20. 10. 2026 (owner): due once there is a trigger, never once linked, at most once in `againAfterDays` after Later. */
export function linkPromptDue(state: Partial<LinkPromptState> | null | undefined, trigger: 'rare' | 'level' | null, linked: boolean, now: number): boolean {
  if (linked || !trigger) return false;
  const last = state?.lastDismissedAt;
  return last == null || now - last >= LINK_PROMPT.againAfterDays * 86_400_000;
}

/**
 * Whether the player has equipped their first item (the tutorial's `equipped` step). Survivors
 * from before the onboarding, and those whose onboarding finished or expired after five runs,
 * count as equipped, so the card never waits forever.
 */
export function firstEquipDone(state: OnboardingState | null | undefined): boolean {
  return !state || state.done || state.steps.equipped != null;
}

/** The equip tutorial shows after the first report until something is equipped. */
export function tutorialActive(state: OnboardingState | null | undefined): 'equip' | null {
  if (!state || state.done) return null;
  return state.steps.exp1_back != null && state.steps.equipped == null ? 'equip' : null;
}
